/**
 * Default allowed domains for redirect URLs (production-safe fallback).
 */
const DEFAULT_ALLOWED_DOMAINS = ['alternativepayments.io', 'altpay.network']

/**
 * Get allowed domains from environment variable or use defaults.
 * Set NEXT_PUBLIC_ALLOWED_REDIRECT_DOMAINS as comma-separated list.
 * Example: "alternativepayments.io,altpay.network,localhost"
 */
function getAllowedDomains(): string[] {
  // eslint-disable-next-line alternative/no-invalid-env-usage
  const envDomains = process.env.NEXT_PUBLIC_ALLOWED_REDIRECT_DOMAINS
  if (envDomains) {
    return envDomains.split(',').map(d => d.trim().toLowerCase())
  }
  return DEFAULT_ALLOWED_DOMAINS
}

/**
 * Validates and sanitizes a redirect URL to prevent XSS attacks.
 *
 * @param url - The URL to validate (can be string, string[], or undefined)
 * @param fallback - Optional fallback URL if validation fails (default: null)
 * @returns The validated URL if safe, otherwise the fallback value
 *
 * @example
 * validateRedirectUrl('https://app.alternativepayments.io/dashboard') // returns the URL
 * validateRedirectUrl('javascript:alert(1)') // returns null
 * validateRedirectUrl('https://evil.com') // returns null
 */
export function validateRedirectUrl(
  url: string | string[] | undefined,
  fallback: string | null = null
): string | null {
  if (!url || Array.isArray(url)) {
    return fallback
  }

  try {
    const parsed = new URL(url)

    // Block dangerous schemes (javascript:, data:, file:, etc.)
    if (!['https:', 'http:'].includes(parsed.protocol)) {
      // eslint-disable-next-line no-console
      console.warn('[Security] Blocked redirect with dangerous scheme:', url)
      return fallback
    }

    // Validate hostname against allowlist
    const hostname = parsed.hostname.toLowerCase()
    const allowedDomains = getAllowedDomains()
    const isAllowed = allowedDomains.some(
      domain => hostname === domain || hostname.endsWith(`.${domain}`)
    )

    if (!isAllowed) {
      // eslint-disable-next-line no-console
      console.warn('[Security] Blocked redirect to untrusted domain:', hostname)
      return fallback
    }

    return url
  } catch {
    // eslint-disable-next-line no-console
    console.warn('[Security] Blocked redirect with invalid URL:', url)
    return fallback
  }
}

/**
 * Validates a redirect URL allowing both safe relative paths and absolute URLs.
 *
 * Relative paths must start with exactly one forward slash.
 * Absolute URLs are delegated to validateRedirectUrl() for domain allowlist checking.
 *
 * Protects against open redirect bypass vectors including protocol-relative URLs,
 * backslash prefixes, URL-encoded slashes, and scheme injection via control characters.
 *
 * @param url - The URL to validate (can be string, string[], or undefined)
 * @param fallback - Optional fallback URL if validation fails (default: null)
 * @returns The validated URL if safe, otherwise the fallback value
 */
export function validateRedirectUrlOrRelative(
  url: string | string[] | undefined,
  fallback: string | null = null
): string | null {
  if (!url || Array.isArray(url)) {
    return fallback
  }

  // Strip leading whitespace and control characters (tabs, newlines)
  const stripped = url.replace(/^[\s\t\n\r]+/, '')

  if (!stripped) {
    return fallback
  }

  // Reject backslash-prefixed paths (open redirect via browser normalization)
  if (stripped.startsWith('\\')) {
    // eslint-disable-next-line no-console
    console.warn('[Security] Blocked redirect with backslash prefix:', url)
    return fallback
  }

  // Reject URL-encoded double-slash (%2f%2f or %2F%2F)
  if (/^%2f%2f/i.test(stripped)) {
    // eslint-disable-next-line no-console
    console.warn('[Security] Blocked redirect with encoded double-slash:', url)
    return fallback
  }

  // Reject control characters within the value (scheme injection like java\nscript:)
  if (/[\t\n\r]/.test(stripped)) {
    // eslint-disable-next-line no-console
    console.warn('[Security] Blocked redirect with control characters:', url)
    return fallback
  }

  // Check if it looks like a relative path (starts with /)
  if (stripped.startsWith('/')) {
    // Reject protocol-relative URLs (//) and mixed slash-backslash (/\)
    if (stripped.startsWith('//') || stripped.startsWith('/\\')) {
      // eslint-disable-next-line no-console
      console.warn('[Security] Blocked protocol-relative redirect:', url)
      return fallback
    }

    // Safe relative path — return the stripped version
    return stripped
  }

  // Not a relative path — delegate to absolute URL validation
  return validateRedirectUrl(stripped, fallback)
}

/**
 * Validates a redirect URL checking protocol only (no domain allowlist).
 *
 * Use for URLs from trusted API data or auth flows where the domain may be
 * a custom partner domain not on the allowlist.
 *
 * Same bypass protections as validateRedirectUrlOrRelative but absolute URLs
 * are only checked for safe protocol (https/http), not against the domain allowlist.
 */
export function validateRedirectProtocol(
  url: string | string[] | undefined,
  fallback: string | null = null
): string | null {
  if (!url || Array.isArray(url)) {
    return fallback
  }

  const stripped = url.replace(/^[\s\t\n\r]+/, '')

  if (!stripped) {
    return fallback
  }

  if (stripped.startsWith('\\')) {
    return fallback
  }

  if (/^%2f%2f/i.test(stripped)) {
    return fallback
  }

  if (/[\t\n\r]/.test(stripped)) {
    return fallback
  }

  if (stripped.startsWith('/')) {
    if (stripped.startsWith('//') || stripped.startsWith('/\\')) {
      return fallback
    }
    return stripped
  }

  try {
    const parsed = new URL(stripped)
    if (!['https:', 'http:'].includes(parsed.protocol)) {
      return fallback
    }
    return stripped
  } catch {
    return fallback
  }
}

/**
 * Safely redirects the browser to a validated URL.
 *
 * Uses validateRedirectProtocol to ensure the URL is safe before redirecting.
 * Centralizes redirect logic to prevent XSS via unvalidated window.location assignments.
 *
 * @param url - The URL to redirect to (validated before use)
 * @returns true if redirect was initiated, false if URL was rejected
 */
export function safeRedirect(url: string | string[] | undefined): boolean {
  const safeUrl = validateRedirectProtocol(url)
  if (!safeUrl) {
    return false
  }

  // Safe: URL has been validated by validateRedirectProtocol which blocks
  // dangerous schemes (javascript:, data:, file:), protocol-relative URLs,
  // control character injection, and other XSS vectors.
  globalThis.location.assign(safeUrl)
  return true
}
